import React, { Suspense } from 'react';
import { useRoutes } from 'react-router-dom';
import { DefaultLayout, VerticalLayout, HorizontalLayout, DetachedLayout, FullLayout } from 'layouts';
import PrivateRoute from './PrivateRoute';
import Root from './Root';
import { LayoutTypes } from 'appConstants';
import { useRedux } from 'hooks';
import ProAccountRoute from './ProAccountRoute';

// lazy load all the views

// public
const MobileRedirectProduct = React.lazy(() => import('pages/public/MobileRedirectProduct'));
const MobileRedirectStore = React.lazy(() => import('pages/public/MobileRedirectStore'));
const MobileRedirect = React.lazy(() => import('pages/public/MobileRedirect'));

// auth
const Login = React.lazy(() => import('pages/account/Login'));
const Logout = React.lazy(() => import('pages/account/Logout'));
const Register = React.lazy(() => import('pages/account/Register'));
const Confirm = React.lazy(() => import('pages/account/Confirm'));
const ForgetPassword = React.lazy(() => import('pages/account/ForgetPassword'));
const LockScreen = React.lazy(() => import('pages/account/LockScreen'));
const RecoverPassword = React.lazy(() => import('pages/account/RecoverPassword'));

const Login2 = React.lazy(() => import('pages/account/Login2'));
const Logout2 = React.lazy(() => import('pages/account/Logout2'));
const Register2 = React.lazy(() => import('pages/account/Register2'));
const Confirm2 = React.lazy(() => import('pages/account/Confirm2'));
const ForgetPassword2 = React.lazy(() => import('pages/account/ForgetPassword2'));
const LockScreen2 = React.lazy(() => import('pages/account/LockScreen2'));

// admin
const AdminHomePage = React.lazy(() => import('pages/admin/home'));
const AdminStores = React.lazy(() => import('pages/admin/stores'));
const AdminStoreView = React.lazy(() => import('pages/admin/stores/view'));
const AdminStoreCreate = React.lazy(() => import('pages/admin/stores/create'));
const AdminAmbassadors = React.lazy(() => import('pages/admin/ambassadors'));
const AdminAmbassadorsView = React.lazy(() => import('pages/admin/ambassadors/view'));
const AdminAmbassadorsCreate = React.lazy(() => import('pages/admin/ambassadors/create'));
const AdminOrders = React.lazy(() => import('pages/admin/orders'));

// store
const StoreHomePage = React.lazy(() => import('pages/store/home'));
const StoreOrders = React.lazy(() => import('pages/store/order'));
const StoreOrderShow = React.lazy(() => import('pages/store/order/show'));
const StoreProductShow = React.lazy(() => import('pages/store/product/show'));
const StoreProductForm = React.lazy(() => import('pages/store/product/form'));
const StoreProducts = React.lazy(() => import('pages/store/product'));
const StoreShop = React.lazy(() => import('pages/store/shop'));
const StoreShopOrders = React.lazy(() => import('pages/store/shop/order'));
const StoreShopOrderShow = React.lazy(() => import('pages/store/shop/order/show'));
const StoreShopCheckout = React.lazy(() => import('pages/store/shop/checkout'));
const StoreFinance = React.lazy(() => import('pages/store/finance'));
const StoreProfilePage = React.lazy(() => import('pages/store/profile'));
const StoreSales = React.lazy(() => import('pages/store/store_sales'));
const StarShop = React.lazy(() => import('pages/store/star_shop'));
const StarShopShow = React.lazy(() => import('pages/store/star_shop/show'));

// dashboard
const AnalyticsDashboard = React.lazy(() => import('pages/template/dashboard/Analytics'));
const EcommerceDashboard = React.lazy(() => import('pages/template/dashboard/Ecommerce'));
const ProjectDashboard = React.lazy(() => import('pages/template/dashboard/Project'));
const EWalletDashboard = React.lazy(() => import('pages/template/dashboard/E-Wallet'));

// apps
const CalendarApp = React.lazy(() => import('pages/template/apps/Calendar'));
const ProjectList = React.lazy(() => import('pages/template/apps/Projects/List'));
const ProjectDetail = React.lazy(() => import('pages/template/apps/Projects/Detail/'));
const ProjectGannt = React.lazy(() => import('pages/template/apps/Projects/Gantt'));
const ProjectForm = React.lazy(() => import('pages/template/apps/Projects/ProjectForm'));

// - chat
const ChatApp = React.lazy(() => import('pages/template/apps/Chat/'));

// -crm
const CRMDashboard = React.lazy(() => import('pages/template/apps/CRM/Dashboard'));
const CRMProjects = React.lazy(() => import('pages/template/apps/CRM/Projects'));
const CRMManagement = React.lazy(() => import('pages/template/apps/CRM/Management'));
const CRMClients = React.lazy(() => import('pages/template/apps/CRM/Clients'));
const CRMOrderList = React.lazy(() => import('pages/template/apps/CRM/OrderList'));

// - ecommece pages
const EcommerceProducts = React.lazy(() => import('pages/template/apps/Ecommerce/Products'));
const ProductDetails = React.lazy(() => import('pages/template/apps/Ecommerce/ProductDetails'));
const Orders = React.lazy(() => import('pages/template/apps/Ecommerce/Orders'));
const OrderDetails = React.lazy(() => import('pages/template/apps/Ecommerce/OrderDetails'));
const Customers = React.lazy(() => import('pages/template/apps/Ecommerce/Customers'));
const Cart = React.lazy(() => import('pages/template/apps/Ecommerce/Cart'));
const Checkout = React.lazy(() => import('pages/template/apps/Ecommerce/Checkout/'));
const Sellers = React.lazy(() => import('pages/template/apps/Ecommerce/Sellers'));

// - email
const Inbox = React.lazy(() => import('pages/template/apps/Email/Inbox'));
const EmailDetail = React.lazy(() => import('pages/template/apps/Email/Detail'));

// - social
const SocialFeed = React.lazy(() => import('pages/template/apps/SocialFeed/'));

// - tasks
const TaskList = React.lazy(() => import('pages/template/apps/Tasks/List/'));
const TaskDetails = React.lazy(() => import('pages/template/apps/Tasks/Details'));
const Kanban = React.lazy(() => import('pages/template/apps/Tasks/Board/'));
// - file
const FileManager = React.lazy(() => import('pages/template/apps/FileManager'));

// pages
const Profile = React.lazy(() => import('pages/template/profile'));
const Profile2 = React.lazy(() => import('pages/template/profile2'));

const ErrorPageNotFound = React.lazy(() => import('pages/template/error/PageNotFound'));
const ErrorPageNotFoundAlt = React.lazy(() => import('pages/template/error/PageNotFoundAlt'));
const ServerError = React.lazy(() => import('pages/template/error/ServerError'));

// - other
const Invoice = React.lazy(() => import('pages/template/other/Invoice'));
const FAQ = React.lazy(() => import('pages/template/other/FAQ'));
const Pricing = React.lazy(() => import('pages/template/other/Pricing'));
const Maintenance = React.lazy(() => import('pages/template/other/Maintenance'));
const Starter = React.lazy(() => import('pages/template/other/Starter'));
const PreLoader = React.lazy(() => import('pages/template/other/PreLoader'));
const Timeline = React.lazy(() => import('pages/template/other/Timeline'));

const Landing = React.lazy(() => import('pages/template/landing'));

// uikit
const Accordions = React.lazy(() => import('pages/template/uikit/Accordions'));
const Alerts = React.lazy(() => import('pages/template/uikit/Alerts'));
const Avatars = React.lazy(() => import('pages/template/uikit/Avatars'));
const Badges = React.lazy(() => import('pages/template/uikit/Badges'));
const Breadcrumbs = React.lazy(() => import('pages/template/uikit/Breadcrumb'));
const Buttons = React.lazy(() => import('pages/template/uikit/Buttons'));
const Cards = React.lazy(() => import('pages/template/uikit/Cards'));
const Carousels = React.lazy(() => import('pages/template/uikit/Carousel'));
const Dropdowns = React.lazy(() => import('pages/template/uikit/Dropdowns'));
const EmbedVideo = React.lazy(() => import('pages/template/uikit/EmbedVideo'));
const Grid = React.lazy(() => import('pages/template/uikit/Grid'));
const ListGroups = React.lazy(() => import('pages/template/uikit/ListGroups'));
const Modals = React.lazy(() => import('pages/template/uikit/Modals'));
const Notifications = React.lazy(() => import('pages/template/uikit/Notifications'));
const Offcanvases = React.lazy(() => import('pages/template/uikit/Offcanvas'));
const Placeholders = React.lazy(() => import('pages/template/uikit/Placeholders'));
const Paginations = React.lazy(() => import('pages/template/uikit/Paginations'));
const Popovers = React.lazy(() => import('pages/template/uikit/Popovers'));
const Progress = React.lazy(() => import('pages/template/uikit/Progress'));
const Ribbons = React.lazy(() => import('pages/template/uikit/Ribbons'));
const Spinners = React.lazy(() => import('pages/template/uikit/Spinners'));
const Tabs = React.lazy(() => import('pages/template/uikit/Tabs'));
const Tooltips = React.lazy(() => import('pages/template/uikit/Tooltips'));
const Typography = React.lazy(() => import('pages/template/uikit/Typography'));
const DragDrop = React.lazy(() => import('pages/template/uikit/DragDrop'));
const RangeSliders = React.lazy(() => import('pages/template/uikit/RangeSliders'));
const Ratings = React.lazy(() => import('pages/template/uikit/Ratings'));

// icons
const Dripicons = React.lazy(() => import('pages/template/icons/Dripicons'));
const MDIIcons = React.lazy(() => import('pages/template/icons/MDIIcons'));
const Unicons = React.lazy(() => import('pages/template/icons/Unicons'));

// forms
const BasicForms = React.lazy(() => import('pages/template/forms/Basic'));
const FormAdvanced = React.lazy(() => import('pages/template/forms/Advanced'));
const FormValidation = React.lazy(() => import('pages/template/forms/Validation'));
const FormWizard = React.lazy(() => import('pages/template/forms/Wizard'));
const FileUpload = React.lazy(() => import('pages/template/forms/FileUpload'));
const Editors = React.lazy(() => import('pages/template/forms/Editors'));

// charts
const ApexChart = React.lazy(() => import('pages/template/charts/Apex'));
const ChartJs = React.lazy(() => import('pages/template/charts/ChartJs'));

// tables
const BasicTables = React.lazy(() => import('pages/template/tables/Basic'));
const AdvancedTables = React.lazy(() => import('pages/template/tables/Advanced'));

// widgets
const Widgets = React.lazy(() => import('pages/template/uikit/Widgets'));

// maps
const GoogleMaps = React.lazy(() => import('pages/template/maps/GoogleMaps'));
const VectorMaps = React.lazy(() => import('pages/template/maps/VectorMaps'));

const loading = () => <div className=""></div>;

type LoadComponentProps = {
    component: React.LazyExoticComponent<() => JSX.Element>;
};

const LoadComponent = ({ component: Component }: LoadComponentProps) => (
    <Suspense fallback={loading()}>
        <Component />
    </Suspense>
);

const AllRoutes = () => {
    const { appSelector } = useRedux();

    const { layout } = appSelector((state) => ({
        layout: state.Layout,
    }));

    const getLayout = () => {
        let layoutCls: React.ComponentType = VerticalLayout;

        switch (layout.layoutType) {
            case LayoutTypes.LAYOUT_HORIZONTAL:
                layoutCls = HorizontalLayout;
                break;
            case LayoutTypes.LAYOUT_DETACHED:
                layoutCls = DetachedLayout;
                break;
            case LayoutTypes.LAYOUT_FULL:
                layoutCls = FullLayout;
                break;
            default:
                layoutCls = VerticalLayout;
                break;
        }
        return layoutCls;
    };
    let Layout = getLayout();

    return useRoutes([
        { path: '/', element: <Root /> },
        {
            // public routes
            path: '/',
            element: <DefaultLayout />,
            children: [
                {
                    path: 'account',
                    children: [
                        { path: 'login', element: <LoadComponent component={Login} /> },
                        { path: 'register', element: <LoadComponent component={Register} /> },
                        { path: 'confirm', element: <LoadComponent component={Confirm} /> },
                        { path: 'forget-password', element: <LoadComponent component={ForgetPassword} /> },
                        { path: 'lock-screen', element: <LoadComponent component={LockScreen} /> },
                        { path: 'reset-password/:id', element: <LoadComponent component={RecoverPassword} /> },
                        { path: 'logout', element: <LoadComponent component={Logout} /> },
                        { path: 'login2', element: <LoadComponent component={Login2} /> },
                        { path: 'register2', element: <LoadComponent component={Register2} /> },
                        { path: 'confirm2', element: <LoadComponent component={Confirm2} /> },
                        { path: 'forget-password2', element: <LoadComponent component={ForgetPassword2} /> },
                        { path: 'lock-screen2', element: <LoadComponent component={LockScreen2} /> },
                        { path: 'logout2', element: <LoadComponent component={Logout2} /> },
                    ],
                },
                {
                    path: 'mob',
                    children: [
                        { path: '', element: <LoadComponent component={MobileRedirect} /> },
                        { path: 'product/:id', element: <LoadComponent component={MobileRedirectProduct} /> },
                        { path: 'store/:id', element: <LoadComponent component={MobileRedirectStore} /> },
                    ],
                },
                {
                    path: 'error-404',
                    element: <LoadComponent component={ErrorPageNotFound} />,
                },
                {
                    path: 'error-500',
                    element: <LoadComponent component={ServerError} />,
                },
                {
                    path: 'maintenance',
                    element: <LoadComponent component={Maintenance} />,
                },
                {
                    path: 'landing',
                    element: <LoadComponent component={Landing} />,
                },
            ],
        },
        {
            // auth protected routes
            path: '',
            element: <PrivateRoute roles={'Store'} component={Layout} />,
            children: [
                {
                    path: 'profile',
                    element: <LoadComponent component={StoreProfilePage} />,
                },
            ],
        },
        {
            // auth + PRO protected routes
            path: '',
            element: <ProAccountRoute roles={'Store'} component={Layout} />,
            children: [
                // {
                //     path: 'home',
                //     element: <LoadComponent component={StoreHomePage} />,
                // },
                // {
                //     path: 'orders',
                //     element: <LoadComponent component={StoreOrders} />,
                // },
                // {
                //     path: 'orders/:id',
                //     element: <LoadComponent component={StoreOrderShow} />,
                // },
                // {
                //     path: 'products',
                //     element: <LoadComponent component={StoreProducts} />,
                // },
                {
                    path: 'shop',
                    element: <LoadComponent component={StoreShop} />,
                },
                // {
                //     path: 'shop/orders',
                //     element: <LoadComponent component={StoreShopOrders} />,
                // },
                {
                    path: 'shop/orders/:id',
                    element: <LoadComponent component={StoreShopOrderShow} />,
                },
                {
                    path: 'shop/checkout',
                    element: <LoadComponent component={StoreShopCheckout} />,
                },
                {
                    path: 'finance',
                    element: <LoadComponent component={StoreFinance} />,
                },
                {
                    path: 'store_sales',
                    element: <LoadComponent component={StoreSales} />,
                },
                {
                    path: 'star_shop',
                    element: <LoadComponent component={StarShop} />,
                },
                {
                    path: 'star_shop/:id',
                    element: <LoadComponent component={StarShopShow} />,
                },
                {
                    path: 'star_shop/:id/orders/:orderId',
                    element: <LoadComponent component={StoreOrderShow} />,
                },
                {
                    path: 'star_shop/:id/products/create',
                    element: <LoadComponent component={StoreProductForm} />,
                },
                {
                    path: 'star_shop/:id/products/:productId',
                    element: <LoadComponent component={StoreProductShow} />,
                },
                {
                    path: 'star_shop/:id/products/:productId/edit',
                    element: <LoadComponent component={StoreProductForm} />,
                },
            ],
        },
        {
            // auth protected routes
            path: 'admin',
            element: <PrivateRoute roles={'Admin'} component={Layout} />,
            children: [
                {
                    path: 'home',
                    element: <LoadComponent component={AdminHomePage} />,
                },
                {
                    path: 'stores',
                    element: <LoadComponent component={AdminStores} />,
                },
                {
                    path: 'stores',
                    children: [
                        {
                            path: 'view',
                            element: <LoadComponent component={AdminStoreView} />,
                        },
                        {
                            path: 'create',
                            element: <LoadComponent component={AdminStoreCreate} />,
                        },
                    ],
                },
                {
                    path: 'ambassadors',
                    element: <LoadComponent component={AdminAmbassadors} />,
                },
                {
                    path: 'ambassadors',
                    children: [
                        {
                            path: 'view',
                            element: <LoadComponent component={AdminAmbassadorsView} />,
                        },
                        {
                            path: 'create',
                            element: <LoadComponent component={AdminAmbassadorsCreate} />,
                        },
                    ],
                },
                {
                    path: 'orders',
                    element: <LoadComponent component={AdminOrders} />,
                },
            ],
        },
        {
            // template auth protected routes
            path: '/',
            element: <PrivateRoute roles={'Admin Store'} component={Layout} />,
            children: [
                {
                    path: 'dashboard',
                    children: [
                        {
                            path: 'analytics',
                            element: <LoadComponent component={AnalyticsDashboard} />,
                        },
                        {
                            path: 'ecommerce',
                            element: <LoadComponent component={EcommerceDashboard} />,
                        },
                        {
                            path: 'project',
                            element: <LoadComponent component={ProjectDashboard} />,
                        },
                        {
                            path: 'e-wallet',
                            element: <LoadComponent component={EWalletDashboard} />,
                        },
                    ],
                },
                {
                    path: 'apps',
                    children: [
                        {
                            path: 'calendar',
                            element: <LoadComponent component={CalendarApp} />,
                        },
                        {
                            path: 'chat',
                            element: <LoadComponent component={ChatApp} />,
                        },
                        {
                            path: 'crm',
                            children: [
                                {
                                    path: 'dashboard',
                                    element: <LoadComponent component={CRMDashboard} />,
                                },
                                {
                                    path: 'projects',
                                    element: <LoadComponent component={CRMProjects} />,
                                },
                                {
                                    path: 'management',
                                    element: <LoadComponent component={CRMManagement} />,
                                },
                                {
                                    path: 'clients',
                                    element: <LoadComponent component={CRMClients} />,
                                },
                                {
                                    path: 'orders',
                                    element: <LoadComponent component={CRMOrderList} />,
                                },
                            ],
                        },
                        {
                            path: 'ecommerce',
                            children: [
                                {
                                    path: 'products',
                                    element: <LoadComponent component={EcommerceProducts} />,
                                },
                                {
                                    path: 'details',
                                    element: <LoadComponent component={ProductDetails} />,
                                },
                                {
                                    path: 'orders',
                                    element: <LoadComponent component={Orders} />,
                                },
                                {
                                    path: 'order/details',
                                    element: <LoadComponent component={OrderDetails} />,
                                },
                                {
                                    path: 'customers',
                                    element: <LoadComponent component={Customers} />,
                                },
                                {
                                    path: 'shopping-cart',
                                    element: <LoadComponent component={Cart} />,
                                },
                                {
                                    path: 'checkout',
                                    element: <LoadComponent component={Checkout} />,
                                },
                                {
                                    path: 'sellers',
                                    element: <LoadComponent component={Sellers} />,
                                },
                            ],
                        },
                        {
                            path: 'email',
                            children: [
                                {
                                    path: 'inbox',
                                    element: <LoadComponent component={Inbox} />,
                                },
                                {
                                    path: 'details',
                                    element: <LoadComponent component={EmailDetail} />,
                                },
                            ],
                        },
                        {
                            path: 'tasks',
                            children: [
                                {
                                    path: 'list',
                                    element: <LoadComponent component={TaskList} />,
                                },
                                {
                                    path: 'kanban',
                                    element: <LoadComponent component={Kanban} />,
                                },
                                {
                                    path: 'details',
                                    element: <LoadComponent component={TaskDetails} />,
                                },
                            ],
                        },

                        {
                            path: 'projects',
                            children: [
                                {
                                    path: 'list',
                                    element: <LoadComponent component={ProjectList} />,
                                },
                                {
                                    path: 'details',
                                    element: <LoadComponent component={ProjectDetail} />,
                                },
                                {
                                    path: 'gantt',
                                    element: <LoadComponent component={ProjectGannt} />,
                                },
                                {
                                    path: 'new',
                                    element: <LoadComponent component={ProjectForm} />,
                                },
                            ],
                        },
                        {
                            path: 'social',
                            element: <LoadComponent component={SocialFeed} />,
                        },
                        {
                            path: 'file',
                            element: <LoadComponent component={FileManager} />,
                        },
                    ],
                },
                {
                    path: 'pages',
                    children: [
                        {
                            path: 'starter',
                            element: <LoadComponent component={Starter} />,
                        },
                        {
                            path: 'profile',
                            element: <LoadComponent component={Profile} />,
                        },
                        {
                            path: 'profile2',
                            element: <LoadComponent component={Profile2} />,
                        },
                        {
                            path: 'pricing',
                            element: <LoadComponent component={Pricing} />,
                        },
                        {
                            path: 'error-404-alt',
                            element: <LoadComponent component={ErrorPageNotFoundAlt} />,
                        },
                        {
                            path: 'timeline',
                            element: <LoadComponent component={Timeline} />,
                        },
                        {
                            path: 'invoice',
                            element: <LoadComponent component={Invoice} />,
                        },
                        {
                            path: 'faq',
                            element: <LoadComponent component={FAQ} />,
                        },
                        {
                            path: 'preloader',
                            element: <LoadComponent component={PreLoader} />,
                        },
                    ],
                },
                {
                    path: 'ui',
                    children: [
                        {
                            path: 'base-ui',
                            children: [
                                {
                                    path: 'accordions',
                                    element: <LoadComponent component={Accordions} />,
                                },
                                {
                                    path: 'alerts',
                                    element: <LoadComponent component={Alerts} />,
                                },
                                {
                                    path: 'avatars',
                                    element: <LoadComponent component={Avatars} />,
                                },
                                {
                                    path: 'badges',
                                    element: <LoadComponent component={Badges} />,
                                },
                                {
                                    path: 'breadcrumb',
                                    element: <LoadComponent component={Breadcrumbs} />,
                                },
                                {
                                    path: 'buttons',
                                    element: <LoadComponent component={Buttons} />,
                                },
                                {
                                    path: 'cards',
                                    element: <LoadComponent component={Cards} />,
                                },
                                {
                                    path: 'carousel',
                                    element: <LoadComponent component={Carousels} />,
                                },
                                {
                                    path: 'dropdowns',
                                    element: <LoadComponent component={Dropdowns} />,
                                },
                                {
                                    path: 'embedvideo',
                                    element: <LoadComponent component={EmbedVideo} />,
                                },
                                {
                                    path: 'grid',
                                    element: <LoadComponent component={Grid} />,
                                },
                                {
                                    path: 'listgroups',
                                    element: <LoadComponent component={ListGroups} />,
                                },
                                {
                                    path: 'modals',
                                    element: <LoadComponent component={Modals} />,
                                },
                                {
                                    path: 'notifications',
                                    element: <LoadComponent component={Notifications} />,
                                },
                                {
                                    path: 'offcanvas',
                                    element: <LoadComponent component={Offcanvases} />,
                                },
                                {
                                    path: 'placeholders',
                                    element: <LoadComponent component={Placeholders} />,
                                },
                                {
                                    path: 'paginations',
                                    element: <LoadComponent component={Paginations} />,
                                },
                                {
                                    path: 'popovers',
                                    element: <LoadComponent component={Popovers} />,
                                },
                                {
                                    path: 'progress',
                                    element: <LoadComponent component={Progress} />,
                                },
                                {
                                    path: 'ribbons',
                                    element: <LoadComponent component={Ribbons} />,
                                },
                                {
                                    path: 'spinners',
                                    element: <LoadComponent component={Spinners} />,
                                },
                                {
                                    path: 'tabs',
                                    element: <LoadComponent component={Tabs} />,
                                },
                                {
                                    path: 'tooltips',
                                    element: <LoadComponent component={Tooltips} />,
                                },
                                {
                                    path: 'typography',
                                    element: <LoadComponent component={Typography} />,
                                },
                            ],
                        },
                        {
                            path: 'widgets',
                            element: <LoadComponent component={Widgets} />,
                        },
                        {
                            path: 'extended',
                            children: [
                                {
                                    path: 'dragdrop',
                                    element: <LoadComponent component={DragDrop} />,
                                },
                                {
                                    path: 'rangesliders',
                                    element: <LoadComponent component={RangeSliders} />,
                                },
                                {
                                    path: 'ratings',
                                    element: <LoadComponent component={Ratings} />,
                                },
                            ],
                        },
                        {
                            path: 'icons',
                            children: [
                                {
                                    path: 'unicons',
                                    element: <LoadComponent component={Unicons} />,
                                },
                                {
                                    path: 'mdi',
                                    element: <LoadComponent component={MDIIcons} />,
                                },
                                {
                                    path: 'dripicons',
                                    element: <LoadComponent component={Dripicons} />,
                                },
                            ],
                        },
                        {
                            path: 'forms',
                            children: [
                                {
                                    path: 'basic',
                                    element: <LoadComponent component={BasicForms} />,
                                },
                                {
                                    path: 'advanced',
                                    element: <LoadComponent component={FormAdvanced} />,
                                },
                                {
                                    path: 'validation',
                                    element: <LoadComponent component={FormValidation} />,
                                },
                                {
                                    path: 'wizard',
                                    element: <LoadComponent component={FormWizard} />,
                                },
                                {
                                    path: 'upload',
                                    element: <LoadComponent component={FileUpload} />,
                                },
                                {
                                    path: 'editors',
                                    element: <LoadComponent component={Editors} />,
                                },
                            ],
                        },
                        {
                            path: 'tables',
                            children: [
                                {
                                    path: 'basic',
                                    element: <LoadComponent component={BasicTables} />,
                                },
                                {
                                    path: 'advanced',
                                    element: <LoadComponent component={AdvancedTables} />,
                                },
                            ],
                        },
                        {
                            path: 'charts',
                            children: [
                                {
                                    path: 'apex',
                                    element: <LoadComponent component={ApexChart} />,
                                },
                                {
                                    path: 'chartjs',
                                    element: <LoadComponent component={ChartJs} />,
                                },
                            ],
                        },
                        {
                            path: 'maps',
                            children: [
                                {
                                    path: 'googlemaps',
                                    element: <LoadComponent component={GoogleMaps} />,
                                },
                                {
                                    path: 'vectormaps',
                                    element: <LoadComponent component={VectorMaps} />,
                                },
                            ],
                        },
                    ],
                },
            ],
        },
    ]);
};

export { AllRoutes };
